import { useEffect, useRef, memo } from "react";
import * as d3 from "d3";

export interface NetworkNode {
  id: string;
  group: string | number;
  name: string;
  color?: string;
  meta?: Record<string, unknown>;
}

export interface NetworkLink {
  source: string;
  target: string;
  value: number;
}

export interface NetworkGraphData {
  nodes: NetworkNode[];
  links: NetworkLink[];
}

interface D3NetworkGraphProps {
  data: NetworkGraphData;
  width?: number;
  height?: number;
  onNodeHover?: (node: NetworkNode | null) => void;
  onNodeClick?: (node: NetworkNode) => void;
}

// Dark-theme accent palette aligned with admin dashboard tokens.
const ACCENT_PALETTE = [
  "#60a5fa", // blue
  "#a78bfa", // violet
  "#22d3ee", // cyan
  "#10b981", // emerald
  "#f59e0b", // amber
  "#fb7185", // rose
  "#e879f9", // fuchsia
];

export const D3NetworkGraph = memo(
  ({ data, width = 800, height = 600, onNodeHover, onNodeClick }: D3NetworkGraphProps) => {
    const svgRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
      if (!svgRef.current || !data.nodes.length) return;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      const fallbackColor = d3.scaleOrdinal<string, string>().range(ACCENT_PALETTE);

      const simulation = d3
        .forceSimulation(data.nodes as unknown as d3.SimulationNodeDatum[])
        .force(
          "link",
          d3
            .forceLink(data.links as unknown as d3.SimulationLinkDatum<d3.SimulationNodeDatum>[])
            .id((d: any) => d.id)
            .distance(110),
        )
        .force("charge", d3.forceManyBody().strength(-340))
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force("collision", d3.forceCollide().radius(34));

      const link = svg
        .append("g")
        .attr("stroke", "rgba(148, 163, 184, 0.35)")
        .attr("stroke-opacity", 0.55)
        .selectAll("line")
        .data(data.links)
        .join("line")
        .attr("stroke-width", (d: any) => Math.max(1, Math.sqrt(d.value)));

      const colorFor = (d: NetworkNode) => d.color ?? fallbackColor(String(d.group));

      const node = svg
        .append("g")
        .attr("stroke", "#03060d")
        .attr("stroke-width", 2)
        .selectAll("circle")
        .data(data.nodes)
        .join("circle")
        .attr("r", 13)
        .attr("fill", (d: any) => colorFor(d))
        .style("cursor", "pointer")
        .style("filter", "drop-shadow(0 0 6px rgba(96,165,250,0.35))")
        .call(drag(simulation) as any)
        .on("mouseenter", function (_event, d: any) {
          d3.select(this).attr("r", 17);
          onNodeHover?.(d as NetworkNode);
        })
        .on("mouseleave", function () {
          d3.select(this).attr("r", 13);
          onNodeHover?.(null);
        })
        .on("click", function (_event, d: any) {
          onNodeClick?.(d as NetworkNode);
        });

      node.append("title").text((d: any) => d.name);

      const labels = svg
        .append("g")
        .selectAll("text")
        .data(data.nodes)
        .join("text")
        .text((d: any) => d.name)
        .attr("font-size", 10)
        .attr("font-family", "ui-monospace, SFMono-Regular, Menlo, monospace")
        .attr("dx", 16)
        .attr("dy", 4)
        .style("fill", "rgba(226,232,240,0.85)")
        .style("pointer-events", "none");

      simulation.on("tick", () => {
        link
          .attr("x1", (d: any) => d.source.x)
          .attr("y1", (d: any) => d.source.y)
          .attr("x2", (d: any) => d.target.x)
          .attr("y2", (d: any) => d.target.y);

        node.attr("cx", (d: any) => d.x).attr("cy", (d: any) => d.y);
        labels.attr("x", (d: any) => d.x).attr("y", (d: any) => d.y);
      });

      function drag(sim: d3.Simulation<d3.SimulationNodeDatum, undefined>) {
        function dragstarted(event: any) {
          if (!event.active) sim.alphaTarget(0.3).restart();
          event.subject.fx = event.subject.x;
          event.subject.fy = event.subject.y;
        }
        function dragged(event: any) {
          event.subject.fx = event.x;
          event.subject.fy = event.y;
        }
        function dragended(event: any) {
          if (!event.active) sim.alphaTarget(0);
          event.subject.fx = null;
          event.subject.fy = null;
        }
        return d3
          .drag<SVGCircleElement, NetworkNode>()
          .on("start", dragstarted)
          .on("drag", dragged)
          .on("end", dragended);
      }

      return () => {
        simulation.stop();
      };
    }, [data, width, height, onNodeHover, onNodeClick]);

    return (
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="overflow-visible"
        style={{ maxWidth: "100%" }}
      />
    );
  },
);

D3NetworkGraph.displayName = "D3NetworkGraph";
